/**
 * Utilitaire pour décoder les données de carte biométrique
 * Format: Base64 avec champs length-prefixed
 */

export interface CardUserInfo {
  nom: string;
  prenoms: string;
  code: string;
  dateExpiration: string;
  dateEtablissement: string;
  type: string;
  profil: string;
  numeroCarte: string;
}

/**
 * Décode les données de carte depuis le format Base64 length-prefixed
 * Format attendu: [length1][data1][length2][data2]...
 *
 * Champs (dans l'ordre):
 * 0. Nom
 * 1. Prénoms
 * 2. PIN/Code agent
 * 3. Date expiration
 * 4. Date établissement
 * 5. Type (CAISSIER, ADMIN, etc.)
 * 6. Profil
 * 7. Numéro de carte
 */
export function decodeCardData(base64Data: string): CardUserInfo | null {
  try {
    const cleaned = base64Data.trim();
    if (!cleaned) return null;

    // Décoder le Base64
    const binaryString = atob(cleaned);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Retirer les zéros de padding à la fin
    let end = bytes.length;
    while (end > 0 && bytes[end - 1] === 0) end--;
    if (end < 3) return null;

    const data = bytes.slice(0, end);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const fields: string[] = [];
    let offset = 0;

    // Parser les champs avec longueur préfixée
    while (offset < data.length && fields.length < 8) {
      const fieldLen = data[offset];
      offset += 1;

      if (offset + fieldLen > data.length) break;

      if (fieldLen === 0) {
        fields.push('');
      } else {
        const fieldValue = decoder.decode(data.slice(offset, offset + fieldLen)).trim();
        fields.push(fieldValue);
        offset += fieldLen;
      }
    }

    console.log('[CardDecoder] Champs décodés:', fields);
    console.log('[CardDecoder] Nombre de champs:', fields.length);

    if (fields.length < 3) {
      console.warn('[CardDecoder] Pas assez de champs décodés:', fields.length);
      return null;
    }

    return {
      nom: fields[0] || '',
      prenoms: fields[1] || '',
      code: fields[2] || '',
      dateExpiration: fields[3] || '',
      dateEtablissement: fields[4] || '',
      type: fields[5] || '',
      profil: fields[6] || '',
      numeroCarte: fields[7] || '',
    };
  } catch (error) {
    console.error('[CardDecoder] Erreur de décodage:', error);
    return null;
  }
}

/**
 * Stocke les données de carte dans le sessionStorage
 */
export function storeCardData(base64Data: string): void {
  try {
    sessionStorage.setItem('biotransac.card.data', base64Data);
    console.log('[CardDecoder] Données de carte stockées');
  } catch (error) {
    console.error('[CardDecoder] Erreur stockage carte:', error);
  }
}

/**
 * Récupère et décode les données de carte depuis le sessionStorage
 */
export function getStoredCardData(): CardUserInfo | null {
  try {
    const base64Data = sessionStorage.getItem('biotransac.card.data');
    if (!base64Data) {
      console.log('[CardDecoder] Aucune donnée de carte stockée');
      return null;
    }

    return decodeCardData(base64Data);
  } catch (error) {
    console.error('[CardDecoder] Erreur récupération carte:', error);
    return null;
  }
}

/**
 * Supprime les données de carte du sessionStorage
 */
export function clearCardData(): void {
  try {
    sessionStorage.removeItem('biotransac.card.data');
    console.log('[CardDecoder] Données de carte supprimées');
  } catch (error) {
    console.error('[CardDecoder] Erreur suppression carte:', error);
  }
}
